import React from 'react';
import {
  KoobTooltip,
  PromotionTag,
  RequiredPromotionTag,
  KoobCard,
} from '@koob/ui';
import styled, { css } from 'styled-components';
import { get } from 'lodash';
import { Stack } from '@koob/margaret';
import { Text } from 'ui';
import { Cell, Grid } from 'components/Hotel/Booking/components';
import { useTranslation } from 'react-i18next';
import placeholderHotel from 'images/placeholderHotel.png';
import { formatDate, daysBetween } from '@koob/dates';
import { formatCurrency } from 'utils';
import {
  calculateBookingTotalPriceWithPromo,
  calculateBookingTravelersCount,
} from 'utils/booking';
import BookingExtras from './BookingExtras';
import ExperienceGuaranteedDepartureIcon from '../../../Experience/ExperienceGuaranteedDepartureIcon';
import { AdjustmentKindEnum, BenefitsKindEnum } from '@koob/enums';

const LightContent = styled.div`
  color: ${({ theme }) => theme.textLighter};
  margin-top: 0 !important;
  flex: 1;
`;

const ItemCard = styled(Stack).attrs({
  size: 'full',
  padding: 0.5,
  gutterSize: 0.5,
})`
  border: 1px solid ${({ theme }) => theme.separator};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`;
const HotelPic = styled.div`
  background-image: url('${props => props.image}');
  border-radius: 20px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  height: 145px;
`;

const BookingContainer = styled.div`
  width: 100%;
  margin-bottom: 30px;
`;

const TitleCard = styled.div`
  font-weight: 800;
  font-size: 12px;
  text-transform: uppercase;
`;

const NormalContent = styled.div`
  margin-top: 0 !important;
  display: flex;
  align-items: center;
`;

const WrapperInfos = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(1.5)};

  > div:nth-child(2n + 1) {
    margin-top: ${({ theme }) => theme.spacing()};
  }
`;

const HotelName = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-top: ${({ theme }) => theme.spacing()};
`;

const HotelAdress = styled.div`
  font-size: 14px;
  font-weight: 400;
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

export const WrapperPrice = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 0;
`;

export const TotalPrice = styled.span`
  text-transform: uppercase;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

export const BookingAmount = styled.div`
  color: #1f2937;
  font-weight: 700;
  ${props =>
    props.bold &&
    css`
      font-size: 30px;
    `}
  ${props => props.strikeThrough && 'text-decoration: line-through;'}
`;

const CardTitle = styled(Text).attrs({ type: 'bodyLarge' })`
  font-weight: 800;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.text};
  ${({ bordered }) =>
    bordered &&
    `
    display: block;
    width: 100%;
    border: 1px solid #e5e7eb;
    padding: 8px 12px;
  `}
`;

const PriceSummaryCard = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: 1rem;
  width: 100%;
`;

const PriceSummaryRow = styled.div`
  display: flex;
  justify-content: ${({ compact }) =>
    compact ? 'flex-start' : 'space-between'};
  align-items: center;
  margin-bottom: 0.05rem;
  line-height: 1.4;

  ${({ hasSeparator }) =>
    hasSeparator &&
    `
    &:last-child {
      margin-bottom: 0;
      padding-top: 0.75rem;
      border-top: 1px solid rgba(0, 0, 0, 0.08);
    }
  `}
`;

const AdjustmentValue = styled(BookingAmount)` =>
  ${props => (props.isDiscount ? 'color: #c93d00;' : 'color: #247d4a;')}
`;

const SummaryTotalAmount = styled(BookingAmount)`
  color: #c93d00;
  font-size: 1.05rem;
  margin-left: auto;
`;

const getRecapElements = (booking, values) => {
  if (booking?.hotel) {
    return {
      name: booking?.hotel?.displayName,
      adress: booking?.hotel?.address,
      img: get(booking?.hotel, 'primaryAttachment.imageUrl'),
      totalPriceWithPromo: calculateBookingTotalPriceWithPromo({
        roomsBooked: values.roomCompositions,
      }),
    };
  }

  return {
    name: booking?.experiencesBooked?.[0]?.experience?.name,
    adress: [
      booking?.experiencesBooked?.[0]?.experience?.inCity?.title,
      booking?.experiencesBooked?.[0]?.experience?.inCity?.country?.title,
    ]
      .filter(Boolean)
      .join(' '),
    img: booking?.experiencesBooked?.[0]?.experience?.pictures?.[0]?.imageUrl,
    totalPriceWithPromo:
      booking?.totalPrice ?? booking?.experiencesBooked?.[0]?.price,
  };
};

const BookingSummary = ({ booking, values, reload, children }) => {
  const { t } = useTranslation('booking', { keyPrefix: 'summary' });
  const recapElements = getRecapElements(booking, values);

  const nbGuest = calculateBookingTravelersCount(booking);
  const nights = daysBetween(booking?.startAt, booking?.endAt);

  const pricingSummary = booking?.pricingSummary;
  const totalPrice = pricingSummary?.totalPrice;
  const baseTotal = pricingSummary?.baseTotal;
  const adjustmentAmount = pricingSummary?.adjustmentAmount;
  const showPriceBreakdown = pricingSummary && adjustmentAmount !== 0;

  return (
    <Stack direction="column" size="full" gutterSize={0.5}>
      <BookingContainer>
        <KoobCard>
          <Grid>
            <Cell sizes={{ default: 1, tabletprod: 1 / 2 }}>
              {Boolean(recapElements.img) ? (
                <HotelPic image={recapElements.img} alt="" />
              ) : (
                <HotelPic image={placeholderHotel} alt="" />
              )}
            </Cell>
            <Cell sizes={{ default: 1, tabletprod: 1 / 2 }}>
              <HotelName>{recapElements.name}</HotelName>
              <HotelAdress>{recapElements.adress}</HotelAdress>
            </Cell>
          </Grid>
          <CardTitle bordered>
            {t('referenceCode', {
              referenceCode: booking?.bookingId ?? booking?.id,
            })}
          </CardTitle>
          <WrapperInfos>
            <TitleCard>{t('stay')}</TitleCard>
            {booking?.startAt && booking?.endAt && (
              <NormalContent direction="row">
                {formatDate(booking?.startAt, 'DD/MM/YYYY')}{' '}
                <i
                  className="fa-light fa-arrow-right p-2"
                  style={{ fontSize: 20, color: '#D6D6D6' }}
                />{' '}
                {formatDate(booking?.endAt, 'DD/MM/YYYY')}
              </NormalContent>
            )}
            {recapElements?.startLocation && recapElements.endLocation && (
              <Stack direction="column" className="my-5">
                <div className="flex space-x-2 items-center w-full my-2">
                  <i
                    className="fa-regular fa-flag"
                    style={{ fontSize: 28, color: 'blue' }}
                  />
                  <span>{recapElements?.startLocation}</span>
                </div>
                <div className="flex space-x-2 items-center w-full">
                  <i
                    className="fa-regular fa-flag"
                    style={{ fontSize: 28, color: 'green' }}
                  />
                  <span>{recapElements?.endLocation}</span>
                </div>
              </Stack>
            )}
            {booking?.startAt &&
              booking?.endAt &&
              values.roomCompositions.length > 0 && (
                <Stack
                  alignY="center"
                  gutterSize={0.5}
                  style={{ marginTop: '0px' }}
                >
                  <i className="fa-light fa-moon" style={{ fontSize: 18 }} />
                  <span>
                    {t('nNightsStay', {
                      count: nights,
                      days: nights + 1,
                    })}
                  </span>
                </Stack>
              )}
            <ItemCard alignY="center">
              {values?.roomCompositions && (
                <Stack
                  gutterSize={0.25}
                  alignY="center"
                  style={{ marginTop: '0px' }}
                >
                  <i className="fa-light fa-users" style={{ fontSize: 18 }} />
                  <span>
                    {t('nGuests', {
                      count: nbGuest,
                    })}
                  </span>
                </Stack>
              )}
              {values?.roomCompositions &&
                values.roomCompositions.length > 0 && (
                  <Stack gutterSize={0.25} alignY="center">
                    <i
                      className="fa-regular fa-sitemap"
                      style={{ fontSize: 18 }}
                    />
                    <span>
                      {t('nRooms', { count: values?.roomCompositions.length })}
                    </span>
                  </Stack>
                )}
            </ItemCard>

            <div className="mt-3 flex space-x-3 items-center">
              {booking.allotmentAvailable && (
                <KoobTooltip
                  tip={t('allotments.tooltip')}
                  position="left"
                  hasArrow={false}
                  color="purple"
                >
                  <div className="bg-green-100 rounded-full h-6 w-6 flex items-center justify-center">
                    <i className="fa fa-bolt text-green-500" />
                  </div>{' '}
                </KoobTooltip>
              )}

              {booking.guaranteedDeparture && (
                <ExperienceGuaranteedDepartureIcon absolute={false} />
              )}
            </div>

            {values?.roomCompositions &&
              values?.roomCompositions.length > 0 && (
                <TitleCard>{t('roomtitle')}</TitleCard>
              )}
            {values?.roomCompositions &&
              values?.roomCompositions.map(({ room }, descriptionKey) => (
                <>
                  <WrapperPrice
                    key={descriptionKey}
                    style={{ marginTop: 0, flex: 'content' }}
                  >
                    <LightContent>
                      {room.bedChoice?.promotionDetails?.kind ===
                      BenefitsKindEnum.UPGRADE_ROOM
                        ? room.bedChoice.promotionDetails.upgradedToRoom
                        : room.name}{' '}
                      {room.bedChoice?.name ? `(${room.bedChoice.name})` : ''}
                    </LightContent>
                    <TotalPrice bold className="w-fit">
                      {formatCurrency({
                        amount: room.bedChoice.totalPriceWithPromo,
                        currency: booking?.currency,
                      })}
                    </TotalPrice>
                  </WrapperPrice>
                  <div className="flex flex-wrap gap-1">
                    {room?.bedChoice?.promotions?.map((promotion, index) => (
                      <>
                        {room.bedChoice?.promotionDetails?.kind ===
                        BenefitsKindEnum.UPGRADE_ROOM ? (
                          <KoobTooltip
                            tip={`Upgraded room from ${room?.name} to ${room.bedChoice?.promotionDetails?.upgradedToRoom}`}
                            position="left"
                            hasArrow={true}
                            color="purple"
                          >
                            <PromotionTag name={promotion} key={index} />
                          </KoobTooltip>
                        ) : (
                          <PromotionTag name={promotion} />
                        )}
                      </>
                    ))}

                    {room?.bedChoice?.mandatorySupplements?.map(
                      (name, index) => (
                        <RequiredPromotionTag
                          key={index}
                          name={name}
                          label={t('requiredSupplement')}
                        />
                      ),
                    )}
                  </div>
                </>
              ))}
            {(booking?.experiencesBooked?.[0]?.promotions ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {booking?.experiencesBooked[0].promotions.map((name, index) => (
                  <PromotionTag key={index} name={name} />
                ))}
              </div>
            )}
            <div className="w-full mt-2">
              <BookingExtras booking={booking} reload={reload} />
            </div>

            {pricingSummary ? (
              <PriceSummaryCard>
                {showPriceBreakdown && (
                  <>
                    <CardTitle>{t('priceBreakdown')}</CardTitle>
                    <PriceSummaryRow>
                      <Text>{t('basePrice')}</Text>
                      <BookingAmount strikeThrough={adjustmentAmount !== 0}>
                        {!booking?.currency
                          ? 0
                          : formatCurrency({
                              amount: baseTotal,
                              currency: booking?.currency,
                            })}
                      </BookingAmount>
                    </PriceSummaryRow>
                  </>
                )}

                {showPriceBreakdown && (
                  <PriceSummaryRow>
                    <Text>
                      {t('exceptionalAdjustment', {
                        kind:
                          adjustmentAmount < 0
                            ? AdjustmentKindEnum.DISCOUNT
                            : AdjustmentKindEnum.SUPPLEMENT,
                      })}
                    </Text>
                    <AdjustmentValue isDiscount={adjustmentAmount < 0}>
                      {!booking?.currency
                        ? 0
                        : formatCurrency({
                            amount: adjustmentAmount,
                            currency: booking?.currency,
                          })}
                    </AdjustmentValue>
                  </PriceSummaryRow>
                )}
                <PriceSummaryRow
                  hasSeparator={showPriceBreakdown}
                  compact={!showPriceBreakdown}
                >
                  <TotalPrice bold>{t('total')}</TotalPrice>
                  <SummaryTotalAmount>
                    {!booking?.currency
                      ? 0
                      : formatCurrency({
                          amount:
                            totalPrice ?? recapElements.totalPriceWithPromo,
                          currency: booking?.currency,
                        })}
                  </SummaryTotalAmount>
                </PriceSummaryRow>
              </PriceSummaryCard>
            ) : (
              <Stack direction="column" style={{ marginTop: '20px' }}>
                <div style={{ width: '100%', marginTop: 0 }}>
                  {Boolean(recapElements.totalPriceWithPromo !== undefined) && (
                    <WrapperPrice style={{ marginTop: 0 }}>
                      <TotalPrice>{`${t('total')} :`}</TotalPrice>
                      <BookingAmount>
                        {!booking?.currency
                          ? 0
                          : formatCurrency({
                              amount: recapElements.totalPriceWithPromo,
                              currency: booking?.currency,
                            })}
                      </BookingAmount>
                    </WrapperPrice>
                  )}
                </div>
              </Stack>
            )}
          </WrapperInfos>

          {children && children}
        </KoobCard>
      </BookingContainer>
    </Stack>
  );
};

export default BookingSummary;
